/**
 * Side drawer that opens when the operator clicks a mountpoint row.
 *
 * Two tabs:
 * - Publisher: streams what the base (SOURCE) is uploading (upstream).
 * - Rovers: selector with all currently-connected consumer sessions for this
 *   mountpoint; the chosen session's downstream frames are rendered.
 */

import { useEffect, useMemo, useState } from "react";

import { StreamConsole } from "@/components/StreamConsole";
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useActiveConnections } from "@/hooks/useDashboard";
import type { ActiveConnection, Mountpoint } from "@/types/api";

interface MountpointStreamDrawerProps {
  mountpoint: Mountpoint | null;
  onClose: () => void;
}

export function MountpointStreamDrawer({
  mountpoint,
  onClose,
}: MountpointStreamDrawerProps) {
  const isOpen = mountpoint != null;
  const { data: activeAll } = useActiveConnections();

  const { publisherSession, rovers } = useMemo(() => {
    const list = (activeAll ?? []) as ActiveConnection[];
    if (!mountpoint) return { publisherSession: null, rovers: [] as ActiveConnection[] };
    const onThisMp = list.filter((c) => c.mountpoint === mountpoint.name);
    return {
      publisherSession: onThisMp.find((c) => c.role === "publisher") ?? null,
      rovers: onThisMp.filter((c) => c.role === "consumer"),
    };
  }, [activeAll, mountpoint]);

  const [selectedRover, setSelectedRover] = useState<string | null>(null);

  // Reset rover selection when drawer opens for a new mountpoint, and default
  // to the first connected rover if there is one.
  useEffect(() => {
    if (!mountpoint) {
      setSelectedRover(null);
      return;
    }
    setSelectedRover(rovers[0]?.session_id ?? null);
    // We intentionally key only on mountpoint id; we don't want to reset the
    // selection every time the rover list refetches in the background.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mountpoint?.id]);

  return (
    <Drawer open={isOpen} onOpenChange={(o) => !o && onClose()}>
      <DrawerContent className="bg-white text-zinc-900">
        {mountpoint && (
          <>
            <DrawerHeader>
              <DrawerTitle className="flex items-center gap-2">
                <span>{mountpoint.name}</span>
                <span
                  className={
                    mountpoint.is_enabled
                      ? "badge badge--ok"
                      : "badge badge--off"
                  }
                >
                  {mountpoint.is_enabled ? "Habilitado" : "Deshabilitado"}
                </span>
              </DrawerTitle>
              <DrawerDescription>
                {mountpoint.description ?? "Sin descripción"} · formato {mountpoint.format}
              </DrawerDescription>
            </DrawerHeader>

            <Tabs defaultValue="publisher" className="flex flex-col flex-1 min-h-0">
              <TabsList className="self-start">
                <TabsTrigger value="publisher">
                  Publisher
                  <span className="ml-2 text-[10px] text-muted-foreground">
                    {publisherSession ? "live" : "off"}
                  </span>
                </TabsTrigger>
                <TabsTrigger value="rovers">
                  Rovers
                  <span className="ml-2 text-[10px] text-muted-foreground">
                    ({rovers.length})
                  </span>
                </TabsTrigger>
              </TabsList>

              <TabsContent value="publisher" className="flex-1 min-h-0">
                {publisherSession ? (
                  <StreamConsole
                    enabled
                    mountpointId={mountpoint.id}
                    role="publisher"
                    sessionId={publisherSession.session_id}
                    direction="upstream"
                  />
                ) : (
                  <div className="text-sm text-muted-foreground p-2">
                    Sin publisher conectado en este momento. Cuando la base haga SOURCE aparecerán los frames aquí.
                  </div>
                )}
              </TabsContent>

              <TabsContent value="rovers" className="flex-1 min-h-0">
                {rovers.length === 0 ? (
                  <div className="text-sm text-muted-foreground p-2">
                    Sin rovers conectados ahora mismo.
                  </div>
                ) : (
                  <div className="flex flex-col gap-2 h-full">
                    <label className="flex items-center gap-2 text-xs text-muted-foreground">
                      Rover:
                      <select
                        className="border border-zinc-300 rounded px-2 py-1 text-xs"
                        value={selectedRover ?? ""}
                        onChange={(e) => setSelectedRover(e.target.value)}
                      >
                        {rovers.map((r) => (
                          <option key={r.session_id} value={r.session_id}>
                            {r.client_username} — {r.remote_addr || "ip?"}
                          </option>
                        ))}
                      </select>
                    </label>
                    {selectedRover && (
                      <StreamConsole
                        enabled
                        mountpointId={mountpoint.id}
                        role="consumer"
                        sessionId={selectedRover}
                        direction="downstream"
                      />
                    )}
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </>
        )}
      </DrawerContent>
    </Drawer>
  );
}
